import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { MerchantService, RetailPayout, RetailPayoutStatus } from '@galaxy/billing';
import { map, switchMap, tap } from 'rxjs/operators';
import { formatDisplayPrice } from '@vendasta/shared';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { PayoutStatusBadgeComponent } from '../payout-status-badge/payout-status-badge.component';

import { TranslateModule } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { formatDate } from '@vendasta/galaxy/utility/date-utils';

interface PayoutDetailsField {
  translationKey: string;
  value: string;
  tooltip?: string;
  class?: null | 'italicized';
}

@Component({
  selector: 'smb-invoicing-payout-details',
  templateUrl: './payout-details.component.html',
  styleUrls: ['./payout-details.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    GalaxyPageModule,
    GalaxyPipesModule,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    MatGridList,
    MatGridTile,
    NgForOf,
    NgIf,
    PayoutStatusBadgeComponent,
    TranslateModule,
    MatTooltip,
  ],
})
export class PayoutDetailsComponent implements OnInit {
  loading$$ = new BehaviorSubject<boolean>(true);
  payout$: Observable<RetailPayout>;
  payoutDetailFields$: Observable<PayoutDetailsField[]>;
  @Input() merchantId$: Observable<string>;
  @Input() payoutId$: Observable<string>;

  constructor(private merchantService: MerchantService) {}

  ngOnInit(): void {
    this.payout$ = combineLatest([this.merchantId$, this.payoutId$]).pipe(
      switchMap(([merchantId, payoutId]) => {
        return this.merchantService.getRetailPayout(merchantId, payoutId);
      }),
      tap(() => this.loading$$.next(false)),
    );
    this.payoutDetailFields$ = this.payout$.pipe(
      map((payout) => {
        return [
          {
            translationKey: 'PAYOUT_DETAILS_PAGE.FIELD_NAMES.STATUS',
            value: payout.status,
          },
          {
            translationKey: 'PAYOUT_DETAILS_PAGE.FIELD_NAMES.BANK_ACCOUNT',
            value: payout.lastFourDigits ? `${payout.bankName} ***** ${payout.lastFourDigits}` : 'BANK_ACCOUNT.REMOVED',
            tooltip: !payout.lastFourDigits ? 'BANK_ACCOUNT.REMOVED_TOOLTIP' : null,
            class: !payout.lastFourDigits ? 'italicized' : null,
          },
          {
            translationKey:
              payout.status === RetailPayoutStatus.Paid
                ? 'PAYOUT_DETAILS_PAGE.FIELD_NAMES.COMPLETED'
                : 'PAYOUT_DETAILS_PAGE.FIELD_NAMES.ESTIMATED',
            value: formatDate(payout.arrivalDate, 'MMM d, Y', null),
          },
          {
            translationKey: 'PAYOUT_DETAILS_PAGE.FIELD_NAMES.AMOUNT',
            value: formatDisplayPrice(payout.amount, payout.currencyCode, undefined, true, false, true),
          },
        ];
      }),
    );
  }
}
